<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
                >Search Form
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="loadReport">
                  <v-row class="px-2 py-2">
                    <v-col lg="4" md="4" cols="12">
                      <v-autocomplete
                        v-model="filters.lc_id"
                        :items="lcList"
                        :item-title="item => `${item.lc_no}${item.code ? ' - ' + item.code : ''}`"
                        item-value="id"
                        label="Filter by LC (Optional)"
                        density="compact"
                        prepend-inner-icon="mdi-file-document-outline"
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        clearable
                        hide-details
                      ></v-autocomplete>
                    </v-col>

                    <v-col lg="2" md="2" cols="12">
                      <v-btn
                        class="bg-brand w-15"
                        type="submit"
                        :loading="loading"
                        prepend-icon="mdi-magnify"
                        >Search</v-btn
                      >
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

    <v-container fluid style="margin-top: -20px;">
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel1">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                LC PI Allocation Report
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      class="pl-2"
                      clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                        v-if="reportData.length > 0"
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToCSV"
                          >CSV</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="primary"
                          @click="exportToExcel"
                          >EXCEL</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF"
                          >PDF</v-btn
                        >
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="3" cols="12">
                    <div class="text-right">
                      <v-chip
                        v-if="reportData.length > 0"
                        color="primary"
                        variant="tonal"
                        size="small"
                      >
                        Total LCs: {{ reportData.length }}
                      </v-chip>
                    </div>
                  </v-col>
                </v-row>

                <!-- LC List Table -->
                <v-data-table
                  :headers="lcHeaders"
                  :items="filteredData"
                  :search="search"
                  :loading="loading"
                  item-value="id"
                  class="elevation-1"
                  density="compact"
                  show-expand
                  v-model:expanded="expandedLcRows"
                >
                  <template v-slot:expander="{ item, expanded }">
                    <v-btn
                      :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                      size="small"
                      variant="text"
                      color="primary"
                      @click.stop="toggleLcExpand(item)"
                    ></v-btn>
                  </template>

                  <template v-slot:item.index="{ index }">
                    {{ index + 1 }}
                  </template>

                  <template v-slot:item.lc_no="{ item }">
                    <strong>{{ item.lc_no }}</strong>
                  </template>

                  <template v-slot:item.code="{ item }">
                    {{ item.code || "-" }}
                  </template>

                  <template v-slot:item.lc_amount="{ item }">
                    {{ $numberFormat(item.lc_amount) }}
                  </template>

                  <template v-slot:item.pi_count="{ item }">
                    {{ item.pis?.length || 0 }}
                  </template>

                  <template v-slot:expanded-row="{ item, columns }">
                    <tr>
                      <td :colspan="columns.length" class="pa-4 bg-grey-lighten-4">
                        <div v-if="!item.pis || item.pis.length === 0" class="text-center text-grey pa-4">
                          No PIs allocated to this LC
                        </div>
                        <div v-else>
                          <v-data-table
                            :headers="piHeaders"
                            :items="item.pis"
                            item-value="id"
                            class="elevation-1"
                            density="compact"
                            show-expand
                            v-model:expanded="expandedPiRows"
                          >
                            <template v-slot:expander="{ item: piItem, expanded }">
                              <v-btn
                                :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                                size="small"
                                variant="text"
                                color="info"
                                @click.stop="togglePiExpand(piItem)"
                              ></v-btn>
                            </template>

                            <template v-slot:item.index="{ index }">
                              {{ index + 1 }}
                            </template>

                            <template v-slot:item.pi_no="{ item }">
                              <strong>{{ item.pi_no }}</strong>
                            </template>

                            <template v-slot:item.total_amount="{ item }">
                              {{ $numberFormat(item.total_amount) }}
                            </template>

                            <template v-slot:expanded-row="{ item: piItem, columns: piColumns }">
                              <tr>
                                <td :colspan="piColumns.length" class="pa-4 bg-grey-lighten-5">
                                  <v-table density="compact" class="elevation-1">
                                    <thead>
                                      <tr>
                                        <th>#</th>
                                        <th>Product</th>
                                        <th class="text-right">Quantity</th>
                                        <th class="text-right">Rate</th>
                                        <th class="text-right">Amount</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr v-for="(detail, detailIndex) in (piItem.details || [])" :key="detail.id">
                                        <td>{{ detailIndex + 1 }}</td>
                                        <td>
                                          <span v-if="detail.product">{{ detail.product.name }}</span>
                                          <span v-else class="text-grey">Product ID: {{ detail.product_id }}</span>
                                        </td>
                                        <td class="text-right">{{ formatNumber(detail.qty) }}</td>
                                        <td class="text-right">{{ formatNumber(detail.rate) }}</td>
                                        <td class="text-right">{{ formatNumber(detail.amount) }}</td>
                                      </tr>
                                      <tr v-if="!piItem.details || piItem.details.length === 0">
                                        <td colspan="5" class="text-center text-grey">No items found</td>
                                      </tr>
                                    </tbody>
                                  </v-table>
                                </td>
                              </tr>
                            </template>
                          </v-data-table>
                        </div>
                      </td>
                    </tr>
                  </template>

                  <template v-slot:no-data>
                    <div class="text-center pa-4">
                      <v-icon size="48" color="grey">mdi-database-search</v-icon>
                      <p class="text-grey mt-2">No LC data found</p>
                      <p class="text-caption text-grey">Click Search to load report data</p>
                    </div>
                  </template>
                </v-data-table>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import formatDateView from "@/plugins/dateFormatView";
import { ref, computed, onMounted, getCurrentInstance } from "vue";
import { toast } from "vue3-toastify";
import { useStore } from "vuex";
import Axios from "axios";
import { getLcImportPiAllocationReport } from "@/store/api";

export default {
  name: "LcImportPiAllocationReport",
  setup() {
    const store = useStore();
    const instance = getCurrentInstance();

    const panel = ref([0]);
    const panel1 = ref([0]);
    const loading = ref(false);
    const search = ref("");
    const reportData = ref([]);
    const filters = ref({
      lc_id: null,
    });

    const expandedLcRows = ref([]);
    const expandedPiRows = ref([]);

    const lcHeaders = [
      { title: "#SL", align: "center", key: "index", width: "5%", sortable: false },
      { title: "LC No", key: "lc_no", align: "left", sortable: true },
      { title: "LC Code", key: "code", align: "left", sortable: true },
      { title: "LC Amount", key: "lc_amount", align: "right", sortable: true },
      { title: "PI Count", key: "pi_count", align: "center", sortable: false },
      { title: "Actions", align: "center", key: "data-table-expand", sortable: false, width: "10%" },
    ];

    const piHeaders = [
      { title: "#SL", align: "center", key: "index", width: "5%", sortable: false },
      { title: "PI No", key: "pi_no", align: "left", sortable: true },
      { title: "Total Amount", key: "total_amount", align: "right", sortable: true },
      { title: "Actions", align: "center", key: "data-table-expand", sortable: false, width: "10%" },
    ];

    const lcList = computed(() => {
      return store.getters.getLcImportLcMasterList || [];
    });

    const getCurrentCompany = computed(() => {
      return store.getters.getCurrentCompany || {};
    });

    const getFileServerUrl = computed(() => {
      return store.getters.getFileServerUrl || "";
    });

    const filteredData = computed(() => {
      if (!search.value) return reportData.value;
      
      const searchLower = search.value.toLowerCase();
      return reportData.value.filter(lc => {
        const lcMatch = 
          lc.lc_no?.toLowerCase().includes(searchLower) ||
          lc.code?.toLowerCase().includes(searchLower);
        
        const piMatch = lc.pis?.some(pi => 
          pi.pi_no?.toLowerCase().includes(searchLower)
        );
        
        return lcMatch || piMatch;
      });
    });

    const toggleLcExpand = (lc) => {
      const index = expandedLcRows.value.findIndex(item => item.id === lc.id);
      if (index > -1) {
        expandedLcRows.value.splice(index, 1);
      } else {
        expandedLcRows.value.push(lc);
      }
    };

    const togglePiExpand = (pi) => {
      const index = expandedPiRows.value.findIndex(item => item.id === pi.id);
      if (index > -1) {
        expandedPiRows.value.splice(index, 1);
      } else {
        expandedPiRows.value.push(pi);
      }
    };

    const loadReport = async () => {
      try {
        loading.value = true;
        const params = {};
        if (filters.value.lc_id) {
          params.lc_id = filters.value.lc_id;
        }

        const response = await Axios.get(getLcImportPiAllocationReport, { params });
        reportData.value = response.data || [];
        
        if (reportData.value.length === 0) {
          toast.info("No LC PI allocation data found for the selected criteria");
        } else {
          toast.success(`Loaded ${reportData.value.length} LC record(s)`);
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load LC PI allocation report");
        reportData.value = [];
      } finally {
        loading.value = false;
      }
    };

    const formatNumber = (num) => {
      if (!num && num !== 0) return "0.00";
      return Number(num).toFixed(2);
    };

    const formatDate = (date) => {
      if (!date) return "";
      return formatDateView(date);
    };

    const exportToCSV = () => {
      const items = reportData.value || [];

      if (!items.length) {
        toast.warning("No data to export");
        return;
      }

      const data = [[
        "#SL",
        "LC No",
        "LC Code",
        "LC Amount",
        "PI No",
        "PI Total Amount",
        "Product",
        "Quantity",
        "Rate",
        "Amount",
      ]];

      let rowIndex = 1;
      items.forEach((lc) => {
        if (!lc.pis || lc.pis.length === 0) {
          data.push([
            rowIndex++,
            lc.lc_no || "",
            lc.code || "",
            lc.lc_amount ?? 0,
            "",
            "",
            "",
            "",
            "",
            "",
          ]);
        } else {
          lc.pis.forEach((pi) => {
            if (!pi.details || pi.details.length === 0) {
              data.push([
                rowIndex++,
                lc.lc_no || "",
                lc.code || "",
                lc.lc_amount ?? 0,
                pi.pi_no || "",
                pi.total_amount ?? 0,
                "",
                "",
                "",
                "",
              ]);
            } else {
              pi.details.forEach((detail) => {
                data.push([
                  rowIndex++,
                  lc.lc_no || "",
                  lc.code || "",
                  lc.lc_amount ?? 0,
                  pi.pi_no || "",
                  pi.total_amount ?? 0,
                  detail.product?.name || `Product ID: ${detail.product_id}`,
                  detail.qty ?? 0,
                  detail.rate ?? 0,
                  detail.amount ?? 0,
                ]);
              });
            }
          });
        }
      });

      const proxy = instance?.proxy;
      if (proxy?.$exportTableToCSV && proxy?.$fileDate) {
        proxy.$exportTableToCSV(
          data,
          "lc_pi_allocation_report_" + proxy.$fileDate()
        );
      } else {
        toast.error("CSV export is not configured");
      }
    };

    const exportToExcel = () => {
      const items = reportData.value || [];

      if (!items.length) {
        toast.warning("No data to export");
        return;
      }

      const data = [[
        "#SL",
        "LC No",
        "LC Code",
        "LC Amount",
        "PI No",
        "PI Total Amount",
        "Product",
        "Quantity",
        "Rate",
        "Amount",
      ]];

      let rowIndex = 1;
      items.forEach((lc) => {
        if (!lc.pis || lc.pis.length === 0) {
          data.push([
            rowIndex++,
            lc.lc_no || "",
            lc.code || "",
            lc.lc_amount ?? 0,
            "",
            "",
            "",
            "",
            "",
            "",
          ]);
        } else {
          lc.pis.forEach((pi) => {
            if (!pi.details || pi.details.length === 0) {
              data.push([
                rowIndex++,
                lc.lc_no || "",
                lc.code || "",
                lc.lc_amount ?? 0,
                pi.pi_no || "",
                pi.total_amount ?? 0,
                "",
                "",
                "",
                "",
              ]);
            } else {
              pi.details.forEach((detail) => {
                data.push([
                  rowIndex++,
                  lc.lc_no || "",
                  lc.code || "",
                  lc.lc_amount ?? 0,
                  pi.pi_no || "",
                  pi.total_amount ?? 0,
                  detail.product?.name || `Product ID: ${detail.product_id}`,
                  detail.qty ?? 0,
                  detail.rate ?? 0,
                  detail.amount ?? 0,
                ]);
              });
            }
          });
        }
      });

      const proxy = instance?.proxy;
      if (proxy?.$exportTableToExcel && proxy?.$fileDate) {
        proxy.$exportTableToExcel(
          data,
          "lc_pi_allocation_report_" + proxy.$fileDate()
        );
      } else {
        toast.error("Excel export is not configured");
      }
    };

    const exportToPDF = async () => {
      const items = reportData.value || [];

      if (!items.length) {
        toast.warning("No data to export");
        return;
      }

      const company = getCurrentCompany.value;
      const fileServerUrl = getFileServerUrl.value;

      let printWindow = window.open(
        "",
        "PRINT",
        `width=${screen.width}, height=${screen.height}, left=0, top=0`
      );

      // Build table rows
      let tableRows = "";
      let rowIndex = 1;
      items.forEach((lc) => {
        if (!lc.pis || lc.pis.length === 0) {
          tableRows += `
            <tr>
              <td>${rowIndex++}</td>
              <td>${lc.lc_no || ""}</td>
              <td>${lc.code || ""}</td>
              <td class="text-end">${(lc.lc_amount ?? 0).toLocaleString()}</td>
              <td colspan="6" class="text-center text-grey">No PIs allocated</td>
            </tr>
          `;
        } else {
          lc.pis.forEach((pi, piIndex) => {
            if (!pi.details || pi.details.length === 0) {
              tableRows += `
                <tr>
                  <td>${rowIndex++}</td>
                  <td>${lc.lc_no || ""}</td>
                  <td>${lc.code || ""}</td>
                  <td class="text-end">${(lc.lc_amount ?? 0).toLocaleString()}</td>
                  <td>${pi.pi_no || ""}</td>
                  <td class="text-end">${(pi.total_amount ?? 0).toLocaleString()}</td>
                  <td colspan="4" class="text-center text-grey">No items found</td>
                </tr>
              `;
            } else {
              pi.details.forEach((detail, detailIndex) => {
                tableRows += `
                  <tr>
                    <td>${rowIndex++}</td>
                    <td>${detailIndex === 0 ? (lc.lc_no || "") : ""}</td>
                    <td>${detailIndex === 0 ? (lc.code || "") : ""}</td>
                    <td class="text-end">${detailIndex === 0 ? (lc.lc_amount ?? 0).toLocaleString() : ""}</td>
                    <td>${detailIndex === 0 ? (pi.pi_no || "") : ""}</td>
                    <td class="text-end">${detailIndex === 0 ? (pi.total_amount ?? 0).toLocaleString() : ""}</td>
                    <td>${detail.product?.name || `Product ID: ${detail.product_id}`}</td>
                    <td class="text-end">${(detail.qty ?? 0).toLocaleString()}</td>
                    <td class="text-end">${(detail.rate ?? 0).toLocaleString()}</td>
                    <td class="text-end">${(detail.amount ?? 0).toLocaleString()}</td>
                  </tr>
                `;
              });
            }
          });
        }
      });

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <meta http-equiv="X-UA-Compatible" content="ie=edge">
            <title>LC PI Allocation Report</title>
            <link rel="preconnect" href="https://fonts.googleapis.com">
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
            <link href="https://fonts.googleapis.com/css2?family=Arimo:ital,wght@0,400..700;1,400..700&display=swap" rel="stylesheet">
            <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.8/dist/css/bootstrap.min.css" rel="stylesheet">
            <link href="https://cdn.jsdelivr.net/npm/@mdi/font@7.4.47/css/materialdesignicons.min.css" rel="stylesheet">
            <style>
              body { font-family: 'Arimo', sans-serif; margin: 0px; }
              .invoice-header { display:flex; justify-content:space-between; align-items:center; border-bottom:2px solid #eee; padding-bottom:10px; }
              .invoice-header img { max-height: 70px; max-width: 150px; }
              .table th, .table td { border: 1px solid #000; padding: 8px; vertical-align: middle; font-size: 12px; }
              .table th { background-color: #f0f0f0; color: #333; }
              .text-end { text-align: right; }
              .text-center { text-align: center; }
              h4 { font-size: 20px; color: #333; }
              p { font-size: 14px; color: #555; margin: 2px 0; }
              strong { font-weight: bold; color: #333; }
              @page { margin: 0.5in; size: letter; }
              .logo-container { display:flex; align-items:center; }
              .logo-container img { margin-left: 10px; }
              .table { width: 100%; border-collapse: collapse; table-layout: auto; }
              thead { display: table-header-group; }
              .totals-row { page-break-inside: avoid; }
              .totals-row td { font-weight: bold; background-color: #f5f5f5 !important; }
            </style>
          </head>
          <body>
            <div class="container-fluid position-relative">
              <div class="invoice-header">
                <div>
                  <h4 class="text-uppercase">${company?.name || "Company Name"}</h4>
                  <p style="width:65%">${company?.address || ""}</p>
                  <p><i class="mdi mdi-phone-outline"></i> ${company?.mobile || ""}</p>
                  <p><i class="mdi mdi-email-outline"></i> ${company?.email || ""}</p>
                  ${company?.website ? `<p><i class="mdi mdi-web"></i> ${company.website}</p>` : ""}
                </div>
                <div class="logo-container">
                  ${
                    company?.logo_thumb
                      ? `<img src="${fileServerUrl}${company.logo_thumb}" alt="Logo" class="img-fluid">`
                      : ""
                  }
                </div>
              </div>
              <h4 class="text-center my-4"><strong>LC PI ALLOCATION REPORT</strong></h4>
              <div class="row mb-3">
                <div class="col-sm-8">
                  ${
                    filters.value.lc_id
                      ? (() => {
                          const selectedLc = lcList.value.find(
                            (lc) => lc.id === filters.value.lc_id
                          );
                          return selectedLc
                            ? `<p><strong>LC:</strong> ${selectedLc.lc_no || ""}${selectedLc.code ? " - " + selectedLc.code : ""}</p>`
                            : "";
                        })()
                      : ""
                  }
                </div>
                <div class="col-sm-4 text-end">
                  <p><strong>Total LCs:</strong> ${items.length}</p>
                </div>
              </div>
              <table class="table mt-4">
                <thead>
                  <tr>
                    <th>SN</th>
                    <th>LC No</th>
                    <th>LC Code</th>
                    <th class="text-end">LC Amount</th>
                    <th>PI No</th>
                    <th class="text-end">PI Total Amount</th>
                    <th>Product</th>
                    <th class="text-end">Quantity</th>
                    <th class="text-end">Rate</th>
                    <th class="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${tableRows}
                </tbody>
              </table>
            </div>
          </body>
        </html>
      `);

      printWindow.moveTo(0, 0);
      printWindow.focus();
      await new Promise((resolve) => setTimeout(resolve, 500));
      printWindow.print();
      printWindow.close();
    };

    onMounted(async () => {
      // Load LC list for filter dropdown
      if (store.getters.getLcImportLcMasterList.length === 0) {
        try {
          await store.dispatch("getLcImportLcMasterList");
        } catch (error) {
          console.error("Failed to load LC list:", error);
        }
      }
    });

    return {
      panel,
      panel1,
      loading,
      search,
      reportData,
      filters,
      lcList,
      filteredData,
      lcHeaders,
      piHeaders,
      expandedLcRows,
      expandedPiRows,
      toggleLcExpand,
      togglePiExpand,
      loadReport,
      formatNumber,
      formatDate,
      exportToCSV,
      exportToExcel,
      exportToPDF,
    };
  },
};
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}
</style>
